import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../../components/Layout';
import { Card } from '../../components/ui/card';
import { ShieldCheck, ClipboardCheck, FileCheck, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QualityDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/quality/dashboard`, { withCredentials: true });
      setStats(res.data);
    } catch (e) { /* non-blocking */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const cards = [
    { label: 'Total Inspection Lots', value: stats?.total_lots ?? '—', color: 'text-gray-900' },
    { label: 'Open Lots', value: stats?.open_lots ?? '—', color: 'text-blue-700' },
    { label: 'Completed Lots', value: stats?.completed_lots ?? '—', color: 'text-green-700' },
    { label: 'Rejection Rate', value: stats ? `${stats.rejection_rate}%` : '—', color: 'text-red-600' },
    { label: 'Accepted Qty', value: stats?.accepted_quantity ?? '—', color: 'text-green-700' },
    { label: 'Rejected Qty', value: stats?.rejected_quantity ?? '—', color: 'text-orange-700' },
    { label: 'Open Notifications', value: stats?.open_notifications ?? '—', color: 'text-amber-700' },
  ];

  const links = [
    { path: '/quality/inspection-lots', icon: ClipboardCheck, label: 'Inspection Lots', desc: 'Record results & usage decisions' },
    { path: '/quality/inspection-plans', icon: FileCheck, label: 'Inspection Plans', desc: 'Characteristics per material' },
    { path: '/quality/notifications', icon: AlertTriangle, label: 'Quality Notifications', desc: 'Defects & complaints' },
    { path: '/quality/certificates', icon: FileCheck, label: 'Certificates', desc: 'Certificates of analysis' },
  ];

  return (
    <Layout>
      <div className="p-6 space-y-6" data-testid="qm-dashboard-page">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-teal-100 text-teal-700"><ShieldCheck className="h-5 w-5" /></div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Quality Management</h1>
            <p className="text-gray-600 text-sm mt-0.5">Inspection lifecycle, defects and certificates.</p>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {cards.map((c) => (
            <Card key={c.label} className="p-4">
              <div className="text-xs text-gray-500">{c.label}</div>
              <div className={`text-2xl font-bold mt-1 ${c.color}`} data-testid={`qm-kpi-${c.label}`}>{c.value}</div>
            </Card>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {links.map((l) => (
            <Card key={l.path} className="p-5 cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => navigate(l.path)} data-testid={`qm-link-${l.label}`}>
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-teal-50 text-teal-700"><l.icon className="h-5 w-5" /></div>
                <div>
                  <div className="font-semibold text-gray-900">{l.label}</div>
                  <div className="text-sm text-gray-500">{l.desc}</div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </Layout>
  );
};

export default QualityDashboard;
